/**
 * v11.6 V116-4-4 (ADR-0114 + design round V116-4-1): the insurability pack's legal section (format /3).
 *
 * One renderer for two audiences:
 *  - `internal` (the pack page): gap owners by name, a LIVE "Overdue" marker (computed here, never signed), decision
 *    links for decision-needed laws, and a quiet note when privileged determinations were withheld;
 *  - `verify` (a keyed insurer / broker view): signed fields only, owners as a role, no decision links, and
 *    "Mapping by GAC Pro · not a legal opinion".
 *
 * Rules carried from the design round: scope never shrinks ("AI laws in scope" equals the rows listed; not-counted
 * and decision-needed laws sit under a "Not counted" divider); a control backed only by struck sources reads
 * "No current legal source"; change markers keep performance apart from counting.
 */
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@aigovops/design-system';
import type { PackLawRow, PackLegalChanges, PackLegalControlRow, PackLegalSection } from '../../lib/api';
import { fmtDate, plural } from '../../lib/format';
import {
  changeLines, controlGroup, controlStateLabel, isOverdue, isPassing, packLawCountingLabel,
  packLawStatusLabel, scopeHeadline, tagLineText,
} from '../../lib/pack-legal';
import { decisionHref } from '../controls/ControlSources';
import './insurability-pack.css';

export type LegalAudience = 'internal' | 'verify';

export interface LegalSectionProps {
  legal: Omit<PackLegalSection, 'withheld_privileged'> & { withheld_privileged?: number };
  changes?: PackLegalChanges | null;
  audience: LegalAudience;
  /** System names for "Triggered by". */
  systemNames?: Map<string, string>;
  /** Internal only: the workspace slug and this page's path (decision links come back here). */
  slug?: string;
  backPath?: string;
  /** Internal only: owner / admin can include withheld determinations when signing. */
  onIncludePrivileged?: (() => void) | null;
  /** "Now", for the live overdue check (tests pin it). */
  now?: Date;
  /** Heading level of the section title (h3 inside a stale region on verify). */
  headingLevel?: 2 | 3;
}

function Chip({ s, children }: { s?: 'pass' | 'fail' | 'paused' | 'neutral'; children: ReactNode }) {
  return <span className="ip-chip" data-s={s ?? 'neutral'}>{children}</span>;
}

function StateChip({ state }: { state: string }) {
  const pass = isPassing(state);
  const fail = state === 'fail';
  return (
    <Chip s={pass ? 'pass' : fail ? 'fail' : 'neutral'}>
      <span aria-hidden="true">{pass ? '✓' : fail ? '✕' : '–'}</span>
      {controlStateLabel(state)}
    </Chip>
  );
}

export function LegalSection(props: LegalSectionProps) {
  const { legal, changes, audience, systemNames, slug, backPath, onIncludePrivileged, now = new Date(), headingLevel = 2 } = props;
  const H = headingLevel === 3 ? 'h3' : 'h2';
  const head = scopeHeadline(legal);
  const titles = new Map(legal.rows.map((r) => [r.control_id, r.title ?? r.control_id]));
  const ch = changeLines(changes, titles);
  const counted = legal.laws.filter((l) => l.counted);
  const notCounted = legal.laws.filter((l) => !l.counted);
  const withheld = audience === 'internal' ? legal.withheld_privileged ?? 0 : 0;

  return (
    <section className="ip-legal" id="ip-legal" aria-labelledby="ip-legal-h">
      <div className="ip-legal-head">
        <H id="ip-legal-h" className="ip-legal-h">
          AI laws in scope: <span className="ip-legal-count" data-testid="ip-scope-count">{head.count}</span>
        </H>
        <span className="ip-muted" data-testid="ip-scope-clause">{head.text}</span>
        {audience === 'verify' ? <p className="ip-disclaim">Mapping by GAC Pro · not a legal opinion</p> : null}
      </div>

      {/* The heading already carries the scope count (AES-T4-03): the figures are the two other facts. */}
      <dl className="ip-figs" aria-label="Legal section at a glance">
        <div className="ip-fig">
          <dt>In force and counted</dt>
          <dd className="ip-fig-n">{legal.in_force_laws}</dd>
          <dd className="ip-fig-sub">{plural(legal.counted_laws, 'counted law')}</dd>
        </div>
        <div className="ip-fig">
          <dt>Mapped controls passing</dt>
          <dd className="ip-fig-n">{legal.controls ? `${legal.controls_passing} of ${legal.controls}` : '—'}</dd>
          <dd className="ip-fig-sub">{legal.controls ? 'Counted controls only' : 'No counted control'}</dd>
        </div>
      </dl>

      {ch ? <Changes ch={ch} /> : null}

      {withheld > 0 ? (
        <p className="ip-priv" data-testid="ip-withheld">
          <span><span aria-hidden="true">⛨ </span>{plural(withheld, 'privileged determination')} withheld from this pack</span>
          {onIncludePrivileged ? <Button variant="secondary" compact onClick={onIncludePrivileged}>Include when signing</Button> : <span>· an owner or admin decides</span>}
        </p>
      ) : null}

      {legal.laws.length === 0 ? (
        <p className="ip-muted">No AI law applies to your systems yet. Controls below come from your frameworks.</p>
      ) : (
        <div className="ip-table-wrap" tabIndex={0} role="region" aria-labelledby="ip-legal-h">
          <table className="ip-table">
            <caption className="sr-only">AI laws in scope; laws not counted are listed last</caption>
            <thead>
              <tr>
                <th scope="col">Law</th>
                <th scope="col">Triggered by</th>
                <th scope="col">Effective</th>
                <th scope="col">Status</th>
                <th scope="col">Mapped controls passing</th>
              </tr>
            </thead>
            <tbody>
              {counted.map((l) => <LawRow key={l.law_id} l={l} {...{ audience, systemNames, slug, backPath }} />)}
            </tbody>
            {notCounted.length ? (
              <tbody className="ip-nc-group">
                <tr className="ip-nc-divider"><th scope="rowgroup" colSpan={5}>Not counted · {notCounted.length}</th></tr>
                {notCounted.map((l) => <LawRow key={l.law_id} l={l} {...{ audience, systemNames, slug, backPath }} />)}
              </tbody>
            ) : null}
          </table>
        </div>
      )}

      {legal.tracking.length ? legal.tracking.map((t) => (
        <p key={t.risk_item_id} className="ip-tracking" data-testid="ip-tracking">
          <strong>{t.title}</strong> · covered by the controls below
        </p>
      )) : null}

      <Controls rows={legal.rows} audience={audience} now={now} level={headingLevel + 1} slug={slug} systemNames={systemNames} />
    </section>
  );
}

function Changes({ ch }: { ch: NonNullable<ReturnType<typeof changeLines>> }) {
  if (!ch.comparable) {
    return <p className="ip-muted" data-testid="ip-changes">Not comparable: version {ch.since} counted laws differently</p>;
  }
  if (!ch.performance.length && !ch.counting.length) {
    return <p className="ip-muted" data-testid="ip-changes">No change since version {ch.since}</p>;
  }
  return (
    <div data-testid="ip-changes" className="ip-stack-tight">
      {ch.performance.length ? (
        <div>
          <p className="ip-dim ip-flat" id="ip-chg-perf">Performance since version {ch.since}</p>
          <ul className="ip-changes" aria-labelledby="ip-chg-perf">
            {ch.performance.map((p) => (
              <li key={p.text} className="ip-chg" data-d={p.direction}>
                <span aria-hidden="true">{p.direction === 'better' ? '▲ ' : '▼ '}</span>{p.text}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {ch.counting.length ? (
        <div>
          <p className="ip-dim ip-flat" id="ip-chg-count">Counting changed since version {ch.since}</p>
          <ul className="ip-changes" aria-labelledby="ip-chg-count">
            {ch.counting.map((t) => <li key={t} className="ip-chg" data-d="counting">{t}</li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function LawRow({ l, audience, systemNames, slug, backPath }: { l: PackLawRow; audience: LegalAudience; systemNames?: Map<string, string>; slug?: string; backPath?: string }) {
  const counting = packLawCountingLabel(l);
  const systems = l.system_ids.map((id) => systemNames?.get(id) ?? id).join(' · ');
  const decision = audience === 'internal' && slug && l.not_counted_reason === 'decision_needed' && l.rule_ids[0]
    ? decisionHref(slug, l.rule_ids[0], backPath, backPath ? 'Insurability pack' : null)
    : null;
  const fraction = l.controls.of === 0 ? 'None mapped' : `${l.controls.passing} of ${l.controls.of}`;
  return (
    <tr id={`ip-law-${l.law_id}`} data-counted={l.counted ? 'true' : 'false'}>
      <th scope="row" className="ip-c-law">
        {l.url ? (
          <a className="ip-law-link" href={l.url} target="_blank" rel="noopener noreferrer">
            {l.short_name}<span aria-hidden="true"> ↗</span><span className="sr-only"> (opens in a new tab)</span>
          </a>
        ) : l.short_name}
        {l.citation && l.citation !== l.short_name ? <span className="ip-dim ip-block ip-normal">{l.citation}</span> : null}
      </th>
      <td className="ip-c-trig" data-label="Triggered by">
        {systems ? <span>{systems}</span> : null}
        {l.trigger ? <span className="ip-dim ip-block">{l.trigger}</span> : null}
        {!systems && !l.trigger ? <span className="ip-dim">—</span> : null}
      </td>
      <td className="ip-c-eff ip-nowrap" data-label="Effective">{fmtDate(l.effective_date)}</td>
      <td className="ip-c-status" data-label="Status">
        <Chip s={l.standing === 'stayed' ? 'paused' : 'neutral'}>{packLawStatusLabel(l)}</Chip>
        {counting ? (
          decision ? (
            <Link to={decision} className="ip-block ip-law-link">
              {counting}<span className="sr-only"> for {l.short_name}: choose how it is counted</span>
            </Link>
          ) : <span className="ip-dim ip-block">{counting}</span>
        ) : null}
      </td>
      {/* AES-T4-07: the column says what the fraction is; a not-counted row shows it dim (the divider says why). */}
      <td className={`ip-c-pass ip-num${l.counted ? '' : ' ip-dim'}`} data-label="Mapped controls passing">
        {fraction}<span className="sr-only"> mapped controls passing</span>
      </td>
    </tr>
  );
}

function Controls({ rows, audience, now, level, slug, systemNames }: {
  rows: PackLegalControlRow[]; audience: LegalAudience; now: Date; level: number; slug?: string; systemNames?: Map<string, string>;
}) {
  const H = (level >= 4 ? 'h4' : 'h3') as 'h3' | 'h4';
  const Sub = (level >= 4 ? 'h5' : 'h4') as 'h4' | 'h5';
  const open = rows.filter((r) => controlGroup(r) === 'open');
  const passing = rows.filter((r) => controlGroup(r) === 'passing');
  const paused = rows.filter((r) => controlGroup(r) === 'paused');
  const noSource = rows.filter((r) => controlGroup(r) === 'no_source');
  const countedN = open.length + passing.length;
  const fold = [
    passing.length && `${passing.length} passing`,
    paused.length && `Not counted · ${paused.length}`,
    noSource.length && `No current legal source · ${noSource.length}`,
  ].filter(Boolean).join(' · ');
  const list = (items: PackLegalControlRow[], label?: string, labelledBy?: string) => (
    <ul className="ip-ctls" aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy}>
      {items.map((r) => <ControlRow key={r.control_id} r={r} audience={audience} now={now} slug={slug} systemNames={systemNames} />)}
    </ul>
  );
  return (
    <div className="ip-stack">
      <div className="ip-legal-head">
        <H className="ip-ctl-h" id="ip-ctl-h">Controls</H>
        <span className="ip-dim">
          {countedN} counted{open.length ? ` · ${open.length} not passing` : countedN ? ' · all passing' : ''}
        </span>
      </div>
      {rows.length === 0 ? <p className="ip-muted">No control is mapped to these laws yet.</p> : null}
      {open.length ? list(open, 'Controls not passing') : null}
      {fold ? (
        <details className="ip-fold">
          <summary>{fold}</summary>
          {passing.length ? (<><Sub className="ip-fold-h" id="ip-fold-pass">Passing</Sub>{list(passing, undefined, 'ip-fold-pass')}</>) : null}
          {paused.length ? (<><Sub className="ip-fold-h" id="ip-fold-nc">Not counted</Sub>{list(paused, undefined, 'ip-fold-nc')}</>) : null}
          {noSource.length ? (<><Sub className="ip-fold-h" id="ip-fold-ns">No current legal source</Sub>{list(noSource, undefined, 'ip-fold-ns')}</>) : null}
        </details>
      ) : null}
    </div>
  );
}

const NEEDS_EVIDENCE = new Set(['no_evidence', 'stale', 'insufficient']);

/** FLOW-T4-02: where a control gets fixed: its system's controls tab, one link per system, else the evidence page. */
function FixTitle({ r, slug, systemNames }: { r: PackLegalControlRow; slug: string; systemNames?: Map<string, string> }) {
  const title = r.title ?? r.control_id;
  const ws = `/w/${encodeURIComponent(slug)}/app`;
  const sysHref = (id: string) => `${ws}/systems/${encodeURIComponent(id)}?tab=controls`;
  const ids = r.system_ids ?? [];
  if (ids.length === 1) return <Link className="ip-ctl-title" to={sysHref(ids[0]!)}>{title}<span className="sr-only">: open on {systemNames?.get(ids[0]!) ?? ids[0]}</span></Link>;
  if (ids.length === 0) return <Link className="ip-ctl-title" to={`${ws}/evidence`}>{title}<span className="sr-only">: open evidence</span></Link>;
  return (
    <>
      <span className="ip-ctl-title">{title}</span>
      <span className="ip-dim">
        Open on{' '}
        {ids.map((id, i) => (
          <span key={id}>{i ? ' · ' : ''}<Link to={sysHref(id)}>{systemNames?.get(id) ?? id}<span className="sr-only"> ({title})</span></Link></span>
        ))}
      </span>
    </>
  );
}

function ControlRow({ r, audience, now, slug, systemNames }: { r: PackLegalControlRow; audience: LegalAudience; now: Date; slug?: string; systemNames?: Map<string, string> }) {
  const tags = tagLineText(r);
  const group = controlGroup(r);
  const internal = audience === 'internal' && !!slug;
  const overdue = audience === 'internal' && !!r.gap && isOverdue(r.gap.fix_by, now);
  const owner = r.gap ? (audience === 'internal' ? r.gap.owner_name ?? r.gap.owner_role : r.gap.owner_role) : null;
  return (
    <li className="ip-ctl" id={`ip-ctl-${r.control_id}`} data-group={group}>
      <span className="ip-ctl-main">
        {internal ? <FixTitle r={r} slug={slug!} systemNames={systemNames} /> : <span className="ip-ctl-title">{r.title ?? r.control_id}</span>}
        {audience === 'internal' && r.title ? <span className="ip-ctl-id">{r.control_id}</span> : null}
        {tags ? <span className="ip-dim">{tags}</span> : null}
        {r.gap && group === 'open' ? (
          <span className="ip-gap">
            {owner}
            {' · '}
            {overdue ? <><b className="ip-overdue">Overdue</b>{' · '}</> : null}
            {r.gap.fix_by ? `fix by ${fmtDate(r.gap.fix_by)}` : 'No date committed'}
          </span>
        ) : null}
        {internal && NEEDS_EVIDENCE.has(r.state) && group !== 'no_source' ? (
          <Link className="ip-add-ev" to={`/w/${encodeURIComponent(slug!)}/app/evidence`}>
            Add evidence<span className="sr-only"> for {r.title ?? r.control_id}</span>
          </Link>
        ) : null}
      </span>
      <span>
        {group === 'no_source' ? <Chip>No current legal source</Chip>
          : group === 'paused' ? <Fragment><Chip>Not counted</Chip> <span className="ip-dim">{controlStateLabel(r.state)}</span></Fragment>
          : <StateChip state={r.state} />}
      </span>
    </li>
  );
}
